import { FadeIn } from "../components/FadeIn";
import { ZoomImg } from "../components/ZoomImg";
import { useSeo } from "../lib/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Section, Overline, Divider } from "../components/Primitives";
import {
  IMAGES,
  PRIMARY_FOCUS,
  SECONDARY_FOCUS,
  APPROACH,
  CAMPAIGNS,
} from "../data/content";
import { useContent } from "../context/ContentContext";

export default function AreasOfFocus() {
  useSeo("Areas of Focus", "Education support, mentoring, menstrual dignity, and advocacy — where AMF works to support girls and young women.");
  const { t } = useContent();
  const primary = [
    { n: "01", title: t("aof.focus1.title"), body: t("aof.focus1.body"), image: PRIMARY_FOCUS[0].image },
    { n: "02", title: t("aof.focus2.title"), body: t("aof.focus2.body"), image: PRIMARY_FOCUS[1].image },
    { n: "03", title: t("aof.focus3.title"), body: t("aof.focus3.body"), image: PRIMARY_FOCUS[2].image },
  ];
  return (
    <div data-testid="areas-of-focus-page">
      <PageHeader
        eyebrow={t("aof.header.eyebrow")}
        title={t("aof.header.title")}
        intro={t("aof.header.intro")}
        image={IMAGES.girlFocus}
      />

      {/* PRIMARY FOCUS AREAS */}
      <Section className="bg-amf-cream">
        <div className="space-y-20 md:space-y-28">
          {primary.map((f, i) => (
            <div
              key={f.n}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <FadeIn className="lg:col-span-6 lg:[direction:ltr]">
                <ZoomImg
                  src={f.image}
                  alt={f.title}
                  className="w-full aspect-[4/3] object-cover amf-img"
                />
              </FadeIn>
              <FadeIn delay={0.12} className="lg:col-span-6 lg:[direction:ltr]">
                <span className="font-playfair text-5xl text-amf-gold">{f.n}</span>
                <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3 leading-tight">
                  {f.title}
                </h2>
                <div className="font-lora text-lg text-amf-charcoal/85 mt-5 space-y-4 leading-relaxed">
                  {f.body.split("\n\n").map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </FadeIn>
            </div>
          ))}
        </div>
      </Section>

      {/* SECONDARY FOCUS */}
      <Section className="bg-amf-navy">
        <FadeIn className="max-w-2xl">
          <Overline light>Additional Focus Areas</Overline>
          <h2 className="font-playfair text-3xl md:text-4xl text-amf-cream mt-3">
            {t("aof.secondary.heading")}
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-amf-gold/30 border border-amf-gold/30 mt-12">
          {SECONDARY_FOCUS.map((s, i) => (
            <FadeIn key={s.title} delay={i * 0.1} className="bg-amf-navy p-8 md:p-9">
              <h3 className="font-playfair text-xl text-amf-cream leading-snug">
                {s.title}
              </h3>
              <p className="font-lora text-amf-cream/75 mt-4 leading-relaxed text-[0.95rem]">
                {s.text}
              </p>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* OUR APPROACH */}
      <Section className="bg-amf-cream">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <FadeIn className="lg:col-span-5">
            <Overline>Our Approach</Overline>
            <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3 leading-tight">
              {t("aof.approach.heading")}
            </h2>
          </FadeIn>
          <FadeIn delay={0.12} className="lg:col-span-7">
            <p className="font-lora text-lg text-amf-charcoal/85 leading-relaxed">
              Our strategy combines direct support for girls and young women with
              sustained community engagement and evidence-based policy advocacy.
              We work through four mutually reinforcing commitments:
            </p>
            <Divider className="my-7" />
            <ul className="space-y-4">
              {APPROACH.map((a, i) => (
                <li key={i} className="flex gap-4 items-baseline">
                  <span className="font-playfair text-amf-gold text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-lora text-lg text-amf-charcoal/85">{a}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </Section>

      {/* IMPACT & OUTCOMES — CAMPAIGNS */}
      <Section className="bg-[#F4F6F8]">
        <FadeIn className="max-w-2xl mb-12">
          <Overline>Impact &amp; Outcomes</Overline>
          <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3">
            {t("aof.campaigns.heading")}
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-amf-gold/30 border border-amf-gold/30">
          {CAMPAIGNS.map((c, i) => (
            <FadeIn key={c.title} delay={i * 0.08} className="bg-[#F4F6F8] p-8 md:p-10">
              <span className="amf-overline text-amf-burgundy">
                Campaign {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-playfair text-2xl text-amf-navy mt-3 leading-snug">
                {c.title}
              </h3>
              <p className="font-lora text-amf-charcoal/80 mt-4 leading-relaxed">
                {c.text}
              </p>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
}
